import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  getGetProjectionQueryKey,
  getGetReconciliationQueryKey,
  getListAccountsQueryKey,
  getListAnchorsQueryKey,
  getListCashFlowsQueryKey,
  useCreateAccount,
  useDeleteAccount,
  useListAccounts,
  useUpdateAccount,
} from '../api/generated/fluence'
import type { Account, AccountInput } from '../api/generated/model'
import { formatMoney, toMajor, toMinor } from '../lib/money'

type Mode = { kind: 'idle' } | { kind: 'new' } | { kind: 'edit'; id: string }

type Draft = {
  id: string
  name: string
  type: 'CHECKING' | 'SAVINGS'
  currency: string
  buffer: string
  openingBalance: string
  hasInterest: boolean
  rate: string
  dayCount: 'ACT_365F' | 'ACT_360' | 'ACT_ACT_ISDA'
  postingDay: string
}

function blank(): Draft {
  return {
    id: '',
    name: '',
    type: 'CHECKING',
    currency: 'USD',
    buffer: '0',
    openingBalance: '',
    hasInterest: false,
    rate: '',
    dayCount: 'ACT_365F',
    postingDay: '1',
  }
}

function draftFrom(account: Account): Draft {
  return {
    id: account.id,
    name: account.name,
    type: account.type,
    currency: account.currency,
    buffer: String(toMajor(account.bufferTargetMinor, account.currency)),
    openingBalance: '',
    hasInterest: !!account.interest,
    rate: account.interest ? String(Number(account.interest.annualRate) * 100) : '',
    dayCount: (account.interest?.dayCount ?? 'ACT_365F') as Draft['dayCount'],
    postingDay: String(new Date(`${account.interest?.postingSchedule.anchor ?? '2026-01-01'}T00:00:00`).getDate()),
  }
}

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40)
}

/**
 * Managing accounts.
 *
 * An opening balance is required when creating one, because a projection cannot run for an
 * account with no observed balance — omitting it would not leave the account incomplete, it
 * would break every projection. The field is only shown on create; afterwards the balance
 * is corrected through Observed balances, which is what that panel is for.
 *
 * Deletion states what it will destroy. The cascade reaches cash flows, loans, anchors and
 * recordings, and none of it comes back.
 */
export function AccountEditor({ openOnMount = false }: { openOnMount?: boolean } = {}) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>(openOnMount ? { kind: 'new' } : { kind: 'idle' })
  const [draft, setDraft] = useState<Draft>(blank())
  // With nothing set up yet the form is the whole point of the screen, so it starts open
  // rather than behind a button the user has to find.
  const [error, setError] = useState<string | null>(null)
  const [idTouched, setIdTouched] = useState(false)

  const accounts = useListAccounts()

  const refresh = async () => {
    await Promise.all(
      [
        getListAccountsQueryKey(),
        getListAnchorsQueryKey(),
        getListCashFlowsQueryKey(),
        getGetReconciliationQueryKey(),
        getGetProjectionQueryKey().slice(0, 1),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    )
  }
  const create = useCreateAccount({ mutation: { onSuccess: refresh } })
  const update = useUpdateAccount({ mutation: { onSuccess: refresh } })
  const remove = useDeleteAccount({ mutation: { onSuccess: refresh } })

  const list: Account[] = accounts.data?.status === 200 ? accounts.data.data : []
  const busy = create.isPending || update.isPending || remove.isPending
  const isNew = mode.kind === 'new'

  function startNew() {
    setError(null)
    setIdTouched(false)
    setDraft(blank())
    setMode({ kind: 'new' })
  }

  function startEdit(account: Account) {
    setError(null)
    setIdTouched(true)
    setDraft(draftFrom(account))
    setMode({ kind: 'edit', id: account.id })
  }

  function buildInput(): AccountInput | string {
    if (!draft.name.trim()) return 'Give it a name.'
    if (!draft.id.trim()) return 'An id is required.'
    const bufferTargetMinor = toMinor(draft.buffer || '0', draft.currency)
    if (bufferTargetMinor === null || bufferTargetMinor < 0) return 'Buffer must be a non-negative number.'

    let openingBalanceMinor: number | undefined
    if (isNew) {
      const opening = toMinor(draft.openingBalance, draft.currency)
      if (opening === null) return 'An opening balance is required — a projection needs somewhere to start.'
      openingBalanceMinor = opening
    }

    let interest: AccountInput['interest']
    if (draft.hasInterest) {
      const rate = Number(draft.rate)
      if (!Number.isFinite(rate) || rate < 0) return 'Interest rate must be a non-negative number.'
      const day = Math.min(Math.max(Number(draft.postingDay) || 1, 1), 28)
      interest = {
        annualRate: rate / 100,
        dayCount: draft.dayCount,
        postingSchedule: {
          kind: 'RECURRING',
          anchor: `${new Date().getFullYear()}-01-${String(day).padStart(2, '0')}`,
          frequency: 'MONTHLY',
          endKind: 'NEVER',
        },
      }
    }

    return {
      id: draft.id.trim(),
      name: draft.name.trim(),
      type: draft.type,
      currency: draft.currency,
      bufferTargetMinor,
      openingBalanceMinor,
      interest,
    }
  }

  async function submit() {
    const built = buildInput()
    if (typeof built === 'string') {
      setError(built)
      return
    }
    setError(null)
    const response =
      mode.kind === 'edit'
        ? await update.mutateAsync({ id: mode.id, data: built })
        : await create.mutateAsync({ data: built })
    if (response.status === 200 || response.status === 201) {
      setMode({ kind: 'idle' })
      return
    }
    setError(detail(response) ?? 'The server rejected that.')
  }

  async function confirmDelete(account: Account) {
    const losses = [
      account.dependentCashFlows && `${account.dependentCashFlows} cash flows`,
      account.dependentLoans && `${account.dependentLoans} loans`,
      account.dependentAnchors && `${account.dependentAnchors} balance records`,
    ].filter(Boolean)
    const warning = losses.length
      ? `Deleting "${account.name}" will also remove ${losses.join(', ')}. This cannot be undone.`
      : `Delete "${account.name}"?`
    if (!window.confirm(warning)) return

    const response = await remove.mutateAsync({ id: account.id })
    if (response.status !== 204) {
      setError(detail(response) ?? 'That account could not be deleted.')
    }
  }

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>Accounts</h2>
        {mode.kind === 'idle' && (
          <button type="button" className="btn-primary btn-small" onClick={startNew}>
            Add
          </button>
        )}
      </div>

      {error && <p className="cf-error">{error}</p>}

      {mode.kind !== 'idle' && (
        <form
          className="cf-form"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <div className="cf-grid">
            <label>
              <span>Name</span>
              <input
                value={draft.name}
                autoFocus
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    name: e.target.value,
                    id: isNew && !idTouched ? slugify(e.target.value) : draft.id,
                  })
                }
                placeholder="Everyday Checking"
              />
            </label>
            <label>
              <span>Id</span>
              <input
                value={draft.id}
                disabled={!isNew}
                onChange={(e) => {
                  setIdTouched(true)
                  setDraft({ ...draft, id: e.target.value })
                }}
              />
            </label>
            <label>
              <span>Type</span>
              <select
                value={draft.type}
                disabled={!isNew}
                onChange={(e) => setDraft({ ...draft, type: e.target.value as Draft['type'] })}
              >
                <option value="CHECKING">Checking</option>
                <option value="SAVINGS">Savings</option>
              </select>
            </label>
            <label>
              <span>Currency</span>
              <input
                value={draft.currency}
                disabled={!isNew}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value.toUpperCase() })}
              />
            </label>
            <label>
              <span>Keep at least ({draft.currency})</span>
              <input
                value={draft.buffer}
                inputMode="decimal"
                onChange={(e) => setDraft({ ...draft, buffer: e.target.value })}
                placeholder="2000.00"
              />
            </label>
            {isNew && (
              <label>
                <span>Opening balance ({draft.currency})</span>
                <input
                  value={draft.openingBalance}
                  inputMode="decimal"
                  onChange={(e) => setDraft({ ...draft, openingBalance: e.target.value })}
                  placeholder="what the bank says today"
                />
              </label>
            )}
          </div>

          <label className="cf-checkbox">
            <input
              type="checkbox"
              checked={draft.hasInterest}
              onChange={(e) => setDraft({ ...draft, hasInterest: e.target.checked })}
            />
            <span>Earns interest</span>
          </label>

          {draft.hasInterest && (
            <div className="cf-grid cf-subgrid">
              <label>
                <span>Annual rate (%)</span>
                <input
                  value={draft.rate}
                  inputMode="decimal"
                  onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                  placeholder="3.8"
                />
              </label>
              <label>
                <span>Day count</span>
                <select
                  value={draft.dayCount}
                  onChange={(e) => setDraft({ ...draft, dayCount: e.target.value as Draft['dayCount'] })}
                >
                  <option value="ACT_365F">Actual/365 — usual for US deposits</option>
                  <option value="ACT_360">Actual/360 — pays slightly more</option>
                  <option value="ACT_ACT_ISDA">Actual/Actual</option>
                </select>
              </label>
              <label>
                <span>Posts on day</span>
                <input
                  value={draft.postingDay}
                  inputMode="numeric"
                  onChange={(e) => setDraft({ ...draft, postingDay: e.target.value })}
                />
              </label>
            </div>
          )}

          <div className="cf-actions">
            <button type="button" className="btn-ghost" onClick={() => setMode({ kind: 'idle' })}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : isNew ? 'Add account' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      <ul className="cf-list">
        {list.map((account) => (
          <li key={account.id}>
            <div className="cf-main">
              <span className="cf-name">{account.name}</span>
              <span className="cf-when">
                {account.type.toLowerCase()} · {account.currency}
                {account.bufferTargetMinor > 0 &&
                  ` · keep ${formatMoney(account.bufferTargetMinor, account.currency)}`}
                {account.interest &&
                  ` · ${(Number(account.interest.annualRate) * 100).toFixed(2)}% ${account.interest.dayCount.replace('_', '/')}`}
              </span>
            </div>
            <span className="cf-when">
              {account.dependentCashFlows > 0 && `${account.dependentCashFlows} flows`}
              {account.linkedCards.length > 0 && ` · pays ${account.linkedCards.join(', ')}`}
            </span>
            <div className="cf-buttons">
              <button type="button" className="btn-ghost btn-small" onClick={() => startEdit(account)}>
                Edit
              </button>
              <button
                type="button"
                className="btn-ghost btn-small btn-danger"
                onClick={() => confirmDelete(account)}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Pulls the message out of an RFC 9457 problem response. */
function detail(response: unknown): string | null {
  if (response && typeof response === 'object' && 'data' in response) {
    const data = (response as { data: unknown }).data
    if (data && typeof data === 'object' && 'detail' in data) {
      const value = (data as { detail: unknown }).detail
      if (typeof value === 'string') return value
    }
  }
  return null
}
